/**
 * PostHog Analytics Integration
 * B20251011-Stack-Switch-Phase01: Public pages only (exclude /manage)
 *
 * Centralized PostHog client for analytics and experiments
 */

import posthog from 'posthog-js';

interface PostHogConfig {
  key: string;
  host?: string;
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

let initialized = false;

/**
 * Initialize PostHog client
 */
export function initPostHog(config?: PostHogConfig): boolean {
  if (initialized) return true;

  // Phase 01: Block on /manage routes
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/manage')) {
    if (import.meta.env.DEV) {
      console.info('PostHog blocked on /manage route');
    }
    return false;
  }

  const key = config?.key || POSTHOG_KEY || import.meta.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = config?.host || POSTHOG_HOST || import.meta.env.NEXT_PUBLIC_POSTHOG_HOST;

  if (!key) {
    if (import.meta.env.DEV) {
      console.info('PostHog disabled: no VITE_POSTHOG_KEY or NEXT_PUBLIC_POSTHOG_KEY');
    }
    return false;
  }

  // Only initialize on client-side
  if (typeof window === 'undefined') return false;

  try {
    posthog.init(key, {
      api_host: host,
      capture_pageview: true,
      autocapture: true,
      capture_pageleave: true,
      cross_subdomain_cookie: false,
      persistence: 'localStorage',
      // Respect DNT if available
      respect_dnt: true,
      // TODO: Add consent management integration if needed
      loaded: (posthog) => {
        if (import.meta.env.DEV) {
          console.info('[PostHog] Initialized successfully');
        }
      },
    });

    initialized = true;
    return true;
  } catch (error) {
    console.warn('[PostHog] Initialization failed:', error);
    return false;
  }
}

/**
 * Check if PostHog is ready
 */
export function isPostHogReady(): boolean {
  return initialized && posthog.__loaded;
}

/**
 * Capture custom event
 */
export function capture(eventName: string, properties?: Record<string, any>): void {
  if (!isPostHogReady()) return;

  posthog.capture(eventName, properties);
}

/**
 * Identify user
 */
export function identify(userId?: string, properties?: Record<string, any>): void {
  if (!isPostHogReady()) return;

  if (userId) {
    posthog.identify(userId, properties);
  } else if (properties) {
    posthog.setPersonProperties(properties);
  }
}

/**
 * Set person properties
 */
export function setPersonProps(properties: Record<string, any>): void {
  if (!isPostHogReady()) return;

  posthog.setPersonProperties(properties);
}

/**
 * Get feature flag value
 */
export function getFeatureFlag(key: string, fallback?: any): any {
  if (!isPostHogReady()) return fallback;

  return posthog.getFeatureFlag(key) || fallback;
}

/**
 * Check if feature flag is enabled
 */
export function isFeatureEnabled(key: string): boolean {
  if (!isPostHogReady()) return false;

  return posthog.isFeatureEnabled(key) || false;
}

/**
 * Get PostHog client instance
 */
export function getPostHog(): typeof posthog | null {
  return isPostHogReady() ? posthog : null;
}

export { posthog };
